import {
    createSlice,
    PayloadAction,
    ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import { RootState } from '@config/store';
import { IAtsUserData, IBookingState, ICalls, ITableBooking } from '../types';
import {
    getAtsUsers,
    getBookins,
    getCalls,
    getPromouters,
    getReservedTables,
} from '@api/thunks/booking';
import { CalendarEvent, IBookingObj, PaginatedType, TableParams } from '@types';
import { IPromouter } from '@api/services/bookingApi/types';

const initialState: IBookingState = {
    bookings: [],
    promouters: [],
    atsUsersData: [],
    reservedTables: [],
    bookingEvents: [],
    tableParams: {
        pagination: {
            current: 1,
            pageSize: 20,
        },
        filters: {},
    },
    calls: [],
    totalProm: 0,
    total: 0,
    loading: 'idle',
    error: null,
};

const bookingSlice = createSlice({
    name: '@@booking',
    initialState,
    reducers: {
        clearBookingState: () => {
            return initialState;
        },
        setTableParams: (
            state: IBookingState,
            { payload }: PayloadAction<TableParams>
        ) => {
            state.tableParams = payload;
        },
        resetFilter: (state: IBookingState) => {
            state.tableParams = {
                pagination: {
                    current: 1,
                    pageSize: 20,
                },
                filters: {},
            };
        },
    },
    extraReducers: (builder: ActionReducerMapBuilder<IBookingState>) => {
        builder
            .addCase(
                getBookins.fulfilled,
                (
                    state: IBookingState,
                    { payload }: PayloadAction<PaginatedType<IBookingObj>>
                ) => {
                    state.bookings = payload.rows;
                    state.total = payload.count;
                }
            )
            .addCase(
                getAtsUsers.fulfilled,
                (
                    state: IBookingState,
                    { payload }: PayloadAction<IAtsUserData[]>
                ) => {
                    state.atsUsersData = payload;
                }
            )
            .addCase(
                getReservedTables.fulfilled,
                (
                    state: IBookingState,
                    {
                        payload,
                    }: PayloadAction<{
                        events: CalendarEvent[];
                        data: ITableBooking[];
                    }>
                ) => {
                    state.bookingEvents = payload.events;
                    state.reservedTables = payload.data;
                }
            )

            .addCase(
                getCalls.fulfilled,
                (
                    state: IBookingState,
                    { payload }: PayloadAction<PaginatedType<ICalls>>
                ) => {
                    state.calls = payload.rows;
                }
            )

            .addCase(
                getPromouters.fulfilled,
                (
                    state: IBookingState,
                    { payload }: PayloadAction<PaginatedType<IPromouter>>
                ) => {
                    state.promouters = payload.rows;
                    state.totalProm = payload.count;
                }
            )

            .addMatcher(
                action => action.type.endsWith('/pending'),
                state => {
                    state.loading = 'loading';
                    state.error = null;
                }
            )
            .addMatcher(
                action => action.type.endsWith('/rejected'),
                (state, action: PayloadAction<string>) => {
                    state.loading = 'idle';
                    state.error = action.payload;
                }
            )
            .addMatcher(
                action => action.type.endsWith('/fulfilled'),
                state => {
                    state.loading = 'idle';
                }
            );
    },
});

export const { clearBookingState } = bookingSlice.actions;
export const bookingReducer = bookingSlice.reducer;

export const bookingSelect = (state: RootState) => state.booking;
